import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import Select, { components } from "react-select";
import axios from "axios";

import Generale from "./Generale/Generale";
import DettaglioCostiOre from "./DettaglioCostiOre/DettaglioCostiOre";

import MonthPickerModal from "../../components/MonthPickerModal/MonthPickerModal";

import "../OperazioniHr/OperazioniHr.css";
import "./HrCosti.css";
import Lf23Loader from "../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="oper-hr-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthToFirstDay = (monthValue) => monthValue ? `${monthValue}-01` : "";

const monthToLastDay = (monthValue) => {
    if (!monthValue) return "";
    const [year, month] = monthValue.split("-").map(Number);
    return `${monthValue}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
};

const dateToMonth = (dateValue) => dateValue ? dateValue.substring(0, 7) : "";

const getDefaultDateRange = () => {
    const year = new Date().getFullYear();
    return {
        from: formatDate(new Date(year, 0, 1)),   // 1 gennaio anno corrente
        to: formatDate(new Date(year, 11, 31)),   // 31 dicembre anno corrente
    };
};

const defaultDates = getDefaultDateRange();

const HrCosti = () => {
    const navigate = useNavigate();

    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);

    // Date
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);

    // Select multi-scelta
    const [selectedSettore, setSelectedSettore] = useState([]);
    const [selectedCommessa, setSelectedCommessa] = useState([]);

    const handleFromMonthChange = (e) => setFromDate(monthToFirstDay(e.target.value));
    const handleToMonthChange = (e) => setToDate(monthToLastDay(e.target.value));

    // Opzioni dropdown popolate da /api/hr-costi/filtri-options al mount.
    const [filterOptions, setFilterOptions] = useState({ settore: [], commessa: [] });

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem("token");
                const { data } = await axios.get(`${API_URL}/hr-costi/filtri-options`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (cancelled) return;
                setFilterOptions({
                    settore:  (data?.settori  || []).map(s => ({ value: s, label: s })),
                    commessa: (data?.commesse || []).map(c => ({ value: c, label: c })),
                });
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                console.error("Errore caricamento opzioni filtri HR Costi:", err);
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, []);

    // Filtri applicati (quelli che pilotano la fetch): cambiano solo su "Applica".
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: defaultDates.from,
        toDate: defaultDates.to,
        settore: [],
        commessa: [],
    });

    // Dataset condiviso: un'unica fetch alimenta sia il grafico Generale che la matrice
    // Dettaglio Costi - Ore (stessi filtri, stessa sorgente).
    const [dataset, setDataset] = useState({ rows: [], rowsYoY: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!appliedFilters.fromDate || !appliedFilters.toDate) return;
        const controller = new AbortController();
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("token");
                const settoreCsv = (appliedFilters.settore || []).map(s => s.value).join(",");
                const commessaCsv = (appliedFilters.commessa || []).map(s => s.value).join(",");
                const params = { from: appliedFilters.fromDate, to: appliedFilters.toDate };
                if (settoreCsv) params.settore = settoreCsv;
                if (commessaCsv) params.commessa = commessaCsv;

                const { data } = await axios.get(`${API_URL}/hr-costi/dataset`, {
                    params,
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (cancelled) return;
                setDataset({
                    rows: Array.isArray(data?.rows) ? data.rows : [],
                    rowsYoY: Array.isArray(data?.rowsYoY) ? data.rowsYoY : [],
                });
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                if (cancelled) return;
                console.error("Errore caricamento dataset HR Costi:", err);
                setError(err.response?.data?.error || err.message || "Errore caricamento dati");
                setDataset({ rows: [], rowsYoY: [] });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; controller.abort(); };
    }, [appliedFilters]);

    const handleApply = useCallback(() => {
        setAppliedFilters({
            fromDate, toDate,
            settore: selectedSettore,
            commessa: selectedCommessa,
        });
    }, [fromDate, toDate, selectedSettore, selectedCommessa]);

    const handleReset = useCallback(() => {
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setSelectedSettore([]);
        setSelectedCommessa([]);
        setAppliedFilters({
            fromDate: defaultDates.from,
            toDate: defaultDates.to,
            settore: [],
            commessa: [],
        });
    }, []);

    return (
        <div className="operazioni-hr-page">
            <header className="oper-hr-header">
                <div className="oper-hr-header-content">
                    <button className="oper-hr-back-btn" onClick={() => navigate('/reportistica')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Indietro
                    </button>

                    <div className="oper-hr-header-title">
                        <div className="oper-hr-title-row">
                            <h1>HR Costi</h1>
                            <button className="oper-hr-info-btn" onClick={() => setShowInfoModal(true)} title="Informazioni">
                                <span>i</span>
                            </button>
                        </div>
                        <p className="oper-hr-subtitle">Costi e ore del personale per settore e commessa</p>
                    </div>
                </div>
            </header>

            {showInfoModal && (
                <div className="oper-hr-modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div
                        className="oper-hr-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "flex", flexDirection: "column" }}
                    >
                        <div className="oper-hr-modal-header">
                            <div className="oper-hr-modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="oper-hr-modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div
                            className="oper-hr-modal-body"
                            style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
                        >
                            <div className="oper-hr-modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa pagina analizza il costo del personale e le ore rendicontate,
                                    con dettaglio mensile per settore e commessa. Per ogni periodo sono
                                    disponibili il costo, le ore totali e la variazione anno su anno (YoY)
                                    di entrambi, per individuare scostamenti e trend di spesa.
                                </p>
                            </div>
                            <div className="oper-hr-modal-section">
                                <h4>Filtri globali</h4>
                                <ul>
                                    <li><strong>Filtri:</strong> Periodo (mese inizio/fine), settore e commessa circoscrivono i dati visualizzati</li>
                                    <li><strong>Dettaglio Costi - Ore:</strong> Tabella parametrica configurabile per righe (settore, commessa) e colonne (anno, trimestre, mese), con indicatori Costo, Costi YoY, Ore Totali e Ore YoY</li>
                                </ul>
                            </div>
                            <div className="oper-hr-modal-section">
                                <h4>Nota sui dati</h4>
                                <p>
                                    I dati provengono dal data warehouse con granularita mensile: costi
                                    contabilizzati per operatore e commessa, ore rendicontate sullo stesso
                                    perimetro. Le righe senza anagrafica associata sono raggruppate sotto
                                    la voce "Non assegnato".
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="oper-hr-main">
                <div className="oper-hr-actions">
                    <button
                        type="button"
                        className={`oper-hr-action-btn ${showFiltersPanel ? 'active' : ''}`}
                        onClick={() => setShowFiltersPanel(prev => !prev)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        <span>{showFiltersPanel ? 'Nascondi Filtri' : 'Mostra Filtri'}</span>
                    </button>
                </div>

                {showFiltersPanel && (
                    <div className="oper-hr-panel">
                        <div className="oper-hr-filters-grid">
                            <div className="oper-hr-filter-item">
                                <label>Mese Inizio</label>
                                <MonthPickerModal value={dateToMonth(fromDate)} onChange={handleFromMonthChange} max={dateToMonth(toDate) || undefined} label="Mese Inizio" />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Mese Fine</label>
                                <MonthPickerModal value={dateToMonth(toDate)} onChange={handleToMonthChange} min={dateToMonth(fromDate) || undefined} label="Mese Fine" />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Settore</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={selectedSettore} options={filterOptions.settore} onChange={setSelectedSettore} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Commesse</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutte" value={selectedCommessa} options={filterOptions.commessa} onChange={setSelectedCommessa} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-actions">
                                <button type="button" className="oper-hr-btn-primary" onClick={handleApply}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Applica
                                </button>
                                <button type="button" className="oper-hr-btn-secondary" onClick={handleReset}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                        <path d="M3 3v5h5"></path>
                                    </svg>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div role="alert" style={{ padding: "12px 16px", marginBottom: 16, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, color: "#991b1b", fontSize: 14, fontWeight: 600 }}>
                        {error}
                    </div>
                )}

                <div className="oper-hr-data-section hr-costi-stack">
                    {loading ? (
                        <Lf23Loader />
                    ) : (
                        <>
                            <Generale rows={dataset.rows} />
                            <DettaglioCostiOre rows={dataset.rows} rowsYoY={dataset.rowsYoY} />
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default HrCosti;
